import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {Cohort} from 'common/widgets/cohorts-query-builder/models/cohort';
import {FeatureGate, RestrictEntitySaving} from 'common/report/constants';
import {getReportUrl} from 'common/report/util';
import {cohortsStore} from 'common/report/stores/cohorts';
import {featuresStore} from 'common/report/stores/features';
import {reporter} from 'common/report/error-reporting';
import {
  buildCreateEntityModalAttrs,
  isSharingEnabledForProject,
  normalizeCohortEntity,
} from 'common/report/util/saved-entities';
import {cohortsClient} from 'common/report/queries/app-api/cohorts-client';
import {fetchPeopleCountByCohort} from 'common/report/queries/query-api/people-stats-client';
import {EntityType, SharableEntityType} from 'common/report/util/saved-entities/types';
import {currentProjectStore} from 'common/report/stores/project-details';
import {userDetailsStore} from 'common/report/stores/user-details';
import {SerializedCohort} from 'common/widgets/cohorts-query-builder/models/types/serialized-cohort-types';

import {CohortsQueryBuilderUpdateEvent} from '../cohorts-query-builder-new/types';
import {promptSharing} from '../sharing';
import template from './index.jade';
import css from './index.styl?inline';
import {
  CohortSaveAttempt,
  CohortsModalError,
  CohortsModalState,
  InlineCohortsModalAttrsJsxProps,
  InlineCohortsModalCommitEvent,
} from './types';

export class InlineCohortsModal extends Component<CohortsModalState, InlineCohortsModalAttrsJsxProps['attrs']> {
  public context;
  private _cohortStoreCallback;
  private currentProjectStoreCallback = () => this.update();

  static get attrsSchema(): AttrsSchema<InlineCohortsModalAttrsJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      cohort: `json`,
      'project-id': `number`,
      'workspace-id': `number`,
      'data-group-id': `string`,
      'can-save': `boolean`,
      'can-apply-unsaved-cohort': `boolean`,
      'project-has-cohorts': `boolean`,
      'user-id': `string`,
      'show-cohort-count': `boolean`,
      'track-name': `string`,
      'can-edit': `boolean`,
      name: `string`,
      open: `boolean`,
      type: `string`,
    };
  }

  connectedCallback() {
    super.connectedCallback();

    this.fetchCohorts();

    this.fetchCohortCount();

    // update app when user details are updated (from backend or otherwise)
    userDetailsStore.subscribe(() => this.update()).fetch();
    currentProjectStore.subscribe(this.currentProjectStoreCallback).fetch();
  }

  disconnectedCallback() {
    currentProjectStore.unsubscribe(this.currentProjectStoreCallback);
    cohortsStore.unsubscribe(this._cohortStoreCallback);
    super.disconnectedCallback();
  }

  hasSharingFeatureFlag() {
    return isSharingEnabledForProject(currentProjectStore.value);
  }

  fetchCohorts() {
    this.update({loadingCohorts: true});

    this._cohortStoreCallback = () => {
      this.update({loadingCohorts: false});
    };

    cohortsStore.subscribe(this._cohortStoreCallback);

    cohortsStore.fetch({
      projectId: this.attr(`project-id`),
      workspaceId: this.attr(`workspace-id`),
    });
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(attr, oldValue, newValue);

    switch (attr) {
      case `open`:
      case `name`:
        this.update({name: this.attr(`name`)});
        break;
      case `cohort`: {
        const cohort = Cohort.parseCohort(this.attr(`cohort`));
        this.update({
          cohort,
          description: cohort.description ?? ``,
        });
        break;
      }
    }
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        name: ``,
        showCreateEntityModal: false,
        loadingCohorts: false,
        loadingCohortCount: false,
        cohortCount: null,
        errors: new Set<CohortsModalError>(),
        cohortSaveAttempt: null,
        description: ``,
        cohort: new Cohort(),
        saveCohortAttempted: false,
      },
      helpers: {
        CohortsModalError,
        isNewCohort: () => {
          const {unsavedId, id} = this.state.cohort;
          return id ? false : !unsavedId;
        },
        trackCohortsBuilderEvent: (ev) => {
          const {properties, event} = ev.detail;
          ev.stopPropagation();
          this.trackEvent(event, properties);
        },
        getMPContext: () => this.context,
        getCohorts: () =>
          Object.values(cohortsStore.value).map((cohort: SerializedCohort) => ({
            createdBy: cohort.created_by,
            lastEdited: cohort.last_edited,
            ...cohort,
          })),
        getCohortNames: () =>
          this.helpers
            .getCohorts()
            .filter((cohort) => Number.isInteger(cohort.id))
            .map(({name}) => name),

        getCohortNameError: () => {
          return this.state.cohortSaveAttempt ? this.getNameError() : null;
        },

        getTitle: () => {
          // Only display title if Modal is open
          if (!this.attr(`open`)) {
            return ``;
          }

          const newCohortModalTitle = this.attr(`can-apply-unsaved-cohort`)
            ? `Add Cohort ${this.attr(`type`) || ``}`
            : `Create Cohort`;

          return this.helpers.isNewCohort() ? newCohortModalTitle : `Update Cohort ${this.attr(`type`) || ``}`;
        },

        isReportBasedCohort: () => {
          return this.state.cohort.isReportBased();
        },

        handleCohortUpdated: ({detail: {cohort}}: CohortsQueryBuilderUpdateEvent) => {
          this.update({
            cohort: Cohort.parseCohort({...cohort.serialize(), name: this.state.name}),
          });
          this.fetchCohortCount();
        },
        isSaveDisabled: () => {
          const hasPermission = this.helpers.isNewCohort() || this.attr(`can-edit`);
          return !hasPermission;
        },
        isSavingRestricted: () => !featuresStore.value.canSaveEntity(RestrictEntitySaving.Cohorts),
        openPricingUpsell: () => {
          this.trackEvent(`Click Upsell`, {
            'Feature Limit': FeatureGate.RestrictEntitySaving,
            'Entity Type': `cohorts`,
          });
          window.open(`https://mixpanel.com/pricing/`, `_blank`).focus();
        },
        isOpen: () => this.attr(`open`),
        saveStagedCohortOnInlineModal: async (saveAsNew = false) => {
          this.update({
            cohortSaveAttempt: this.helpers.isNewCohort() || saveAsNew ? CohortSaveAttempt.New : CohortSaveAttempt.Edit,
          });
          if (this.attr(`can-apply-unsaved-cohort`)) {
            this.update({showCreateEntityModal: true});
          } else {
            await this.saveCohort({name: this.state.name, description: this.state.description}, saveAsNew);
          }
        },
        closeEntityModal: () => this.update({showCreateEntityModal: false}),
        openCohortViewUser: () => {
          const {cohort} = this.state;
          const userViewUrl =
            getReportUrl(`explore`, {projectId: this.attr(`project-id`), workspaceId: this.attr(`workspace-id`)}) +
            Cohort.getExploreHashForUsersInCohort({
              cohortId: cohort.id,
              cohortName: cohort.name,
              dataGroupId: cohort.dataGroupId,
            });
          window.open(userViewUrl);
        },
        commitCohort: () => {
          const {name} = this.state.cohort;

          if (!name) {
            return this.addError(CohortsModalError.NameRequired);
          }

          this.dispatchEvent(
            new CustomEvent(`commitCohort`, {
              detail: {name, cohort: this.state.cohort},
            }) as InlineCohortsModalCommitEvent,
          );
        },
        saveStagedCohortOnCreateModal: async (ev) => {
          try {
            const savedCohort = await this.saveCohort(ev.detail.value);

            const {id} = savedCohort;
            // When Save and Share button is clicked, open a sharing modal
            if (ev.type === `saveAndShare`) {
              await promptSharing({
                type: SharableEntityType.Cohort,
                id,
                'project-id': this.attr(`project-id`),
              });
            }
          } catch (error) {
            this.helpers.closeModal();
            reporter.error(error);
          }
          this.helpers.closeEntityModal();
        },
        getModalAttributes: () => {
          return {
            open: this.attr(`open`),
            closeable: true,
            'grow-to-content': true,
            'modal-type': `modal`,
          };
        },
        handleNameInputChange: (ev) => {
          const {value} = ev.target;
          this.update({
            name: value,
            cohortSaveAttempt: null,
            cohort: Cohort.parseCohort({...this.state.cohort.serialize(), name: value}),
          });

          if (value && this.state.errors.has(CohortsModalError.NameRequired)) {
            this.removeError(CohortsModalError.NameRequired);
          }
        },
        handleDescriptionInputChange: ({detail}) => {
          this.update({description: detail.value});
        },
        handleModalStateChange: (ev) => {
          const {state} = ev.detail;

          if (state === `closed`) {
            return this.helpers.closeModal();
          }

          this.dispatchEvent(new CustomEvent(`change`, {detail: {state}}));
        },
        closeModal: () => {
          this.update({errors: new Set()});
          this.dispatchEvent(new CustomEvent(`close`, {detail: {state: `closed`}}));
        },
        buildCreateEntityModalAttrs: () => {
          const {cohort} = this.state;
          cohort.name = this.state.name || cohort.name || ``;
          return buildCreateEntityModalAttrs({
            projectId: this.attr(`project-id`),
            entityType: EntityType.Cohort,
            modalOpen: true,
            isEditing: !!cohort.id,
            isDuplicating: false,
            entity: normalizeCohortEntity(cohort),
            showShareManagement: this.hasSharingFeatureFlag(),
          });
        },
      },
    };
  }

  /**
   * Saves or creates a cohort
   * @param saveAsNew if set, overrides updating an existing cohort and saves it as a new cohort
   */
  async saveCohort({name, description}, saveAsNew = false) {
    let savedCohort: SerializedCohort = null;
    // merge cohort definition with the save modal name & description
    const cohort = Cohort.parseCohort({...this.state.cohort.serialize(), name, description});

    if (this.helpers.getCohortNameError()) {
      return null;
    }

    try {
      if (saveAsNew || cohort.id === null) {
        savedCohort = await cohortsClient.createCohort({
          workspaceId: this.attr(`workspace-id`),
          projectId: this.attr(`project-id`),
          cohort: cohort.serialize(),
        });
      } else {
        savedCohort = await cohortsClient.updateCohortById(
          {
            workspaceId: this.attr(`workspace-id`),
            projectId: this.attr(`project-id`),
            cohortID: cohort.id,
          },
          cohort.serialize(),
        );
      }
      const {id} = savedCohort;
      cohortsStore.update({[id]: savedCohort});
      this.dispatchEvent(
        new CustomEvent(`commitSavedCohort`, {
          detail: {
            id,
            name,
            savedAsNew: saveAsNew,
            cohort: Cohort.parseCohort({
              ...savedCohort,
              /** preserve unsavedId in case a temp cohort is saved */
              unsavedId: saveAsNew ? null : this.state.cohort.unsavedId,
            }),
          },
        } as InlineCohortsModalCommitEvent),
      );
    } catch (error) {
      throw new Error(`Failed to save cohort`);
    }

    return savedCohort;
  }

  async fetchCohortCount() {
    if (!this.attr(`show-cohort-count`)) {
      return;
    }

    this.update({loadingCohortCount: true});
    const cohortCount = await fetchPeopleCountByCohort(this.state.cohort, true, this.attr(`data-group-id`), {
      projectId: this.attr(`project-id`),
      workspaceId: this.attr(`workspace-id`),
    });
    this.update({
      cohortCount,
      loadingCohortCount: false,
    });
  }

  private getNameError() {
    const existingEntityNames = this.helpers.getCohortNames();

    const sameCohortNameExist = () => {
      return (
        existingEntityNames.includes(this.state.name) &&
        (this.state.cohortSaveAttempt === CohortSaveAttempt.New || this.state.name !== this.attr(`name`))
      );
    };

    if (this.state.name.trim().length === 0) {
      return `Enter a name for your cohort.`;
    } else if (sameCohortNameExist()) {
      return `a cohort with that name already exists`;
    }
    //TODO cohort name char limit?
    return null;
  }

  private addError(value: CohortsModalError) {
    const errors = new Set([...this.state.errors]);
    errors.add(value);

    this.update({errors});
  }

  private removeError(value: CohortsModalError) {
    const errors = new Set([...this.state.errors]);
    errors.delete(value);

    this.update({errors});
  }

  private trackEvent(eventName, props) {
    this.dispatchEvent(
      new CustomEvent(`track`, {
        bubbles: true,
        composed: true,
        detail: {
          eventName,
          props,
        },
      }),
    );
  }
}

export const InlineCohortsModalEl = defineAndMakeJsxFactory<InlineCohortsModalAttrsJsxProps>(
  `mp-inline-cohorts-modal`,
  InlineCohortsModal,
);
